import React from 'react';
import { Box, Typography, Grid, Button } from '@mui/material';
import { Download, Share2, ExternalLink, CircleCheckBig } from 'lucide-react';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useUser } from '@/contexts/UserContext';
import type { DigitalPassport, Passport, Skillstree } from '@/types/achievement';
// import { getEncryptedUserId } from '@/utils/encryption';

interface PassportCardProps {
  passport: DigitalPassport|Passport;
}

const PassportCard: React.FC<PassportCardProps> = ({ passport }) => {
  const sliderSettings = {    
    className: "center",
    centerMode: true,
    autoplay: false,
    autoplaySpeed: 5000,
    dots: false,
    infinite: true,
    arrows: true,
    pauseOnHover: true,
    fade: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true, // Key setting for varying heights
  };

  const { user } = useUser();
  const baseUrl = import.meta.env.VITE_APP_URL || 'https://dev-api.skillsture.io';
  
  // Generate encrypted user ID for the credential link
  // const encryptedUserId = user?.id ? getEncryptedUserId(user.id.toString()) : '0';
  // const credentialLink = `${baseUrl}/profile/${encryptedUserId}/passport/${(passport as Passport).unique_code ?? (passport as DigitalPassport).passportId ?? (passport as Passport).id}`;
  // const credentialLink = `${baseUrl}/profile/${user?.id}/passports/${passport.id}`;
  const credentialLink = `${baseUrl}/profile/${user?.id}`;
  
  const role = passport.role || ((passport as Passport).skillstree as Skillstree)?.name || null;
  const image = passport.image || ((passport as Passport).skillstree as Skillstree)?.image_url || "/pages/achievements/skilltrue-passport.png";
  const field = passport.field || passport.category || (passport as Passport).category_name || (passport as Passport).badges[0]?.category || null;
  const verificationUrl = (passport as Passport)?.verification_url || passport.verificationUrl || '#';
  const downloadUrl = (passport as Passport)?.pdf_url || (passport as Passport)?.download_url || passport.downloadUrl || '#';
  const thumbnailUrl = (passport as Passport)?.thumbnail_url;
  const verifiedBy = passport.verifiedBy || "PEOPLElogy Digital Sdn Bhd";
  const passportId = passport.passportId || (passport as Passport).unique_code || (passport as Passport).id;
  
  // Build gallery array with thumbnail if available
  const galleryImages = passport.gallery?.length ? passport.gallery : ["/pages/achievements/skilltrue-passport.png"];
  const gallery = thumbnailUrl ? [thumbnailUrl, ...galleryImages] : galleryImages;

  const handleShareLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(credentialLink)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };

  const handleDownload = async () => {
    if (downloadUrl && downloadUrl !== '#') {
      try {
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `passport_${passportId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
        // Fallback to opening in new tab
        window.open(downloadUrl, '_blank');
      }
    }
  };

  return (
    <div className="flex flex-col bg-blue-900 border-2 border-blue-400/30 rounded-sm shadow-sm px-3 py-4 md:px-6 md:py-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-3">
          <div className="w-40 h-40 md:w-44 md:h-44 flex-shrink-0 bg-transparent border-2 border-blue-400/30 rounded-sm shadow-md flex items-center justify-center">
            <img className="max-h-36 md:max-h-40 h-11/12 w-auto object-center" src={image ?? ''} alt={role || undefined} onError={(e) => e.currentTarget.src = '/pages/achievements/skilltrue-passport.png'}/>
          </div>
          <div className="flex flex-grow flex-col items-center md:items-start">
            <span className="text-base md:text-lg font-normal text-white mb-1">{role}</span>
            <div className="flex flex-wrap flex-row items-start gap-2">
              {field && <span className="text-xs md:text-sm font-medium bg-purple-100 text-purple-800 border border-purple-800 rounded-sm mt-1 py-0.5 px-2.5">
                {field}
              </span>}
              <span className="text-xs md:text-sm font-medium bg-transparent text-blue-300 rounded-sm border border-blue-300 mt-1 py-0.5 px-2.5">
                Digital Passport
              </span>
            </div>                      
            <div className="text-xs md:text-sm font-normal text-white/60 mt-2">
              Verified by 
              <span className="text-sm md:text-base font-normal text-blue-300"> {verifiedBy}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-row md:flex-col items-start md:items-end justify-between md:justify-start gap-2 md:gap-0">
          <span className="text-sm md:text-base font-normal text-blue-300 md:mb-1 break-all">{passportId}</span>
          <span className="text-xs md:text-sm font-normal text-white/60 whitespace-nowrap">Passport ID</span>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 mt-6 justify-items-center justify-center items-stretch">
        <div className="w-full md:col-span-2 md:w-11/12 px-6 md:px-4 py-2 md:py-4 mb-4 md:mb-6">
          <Slider {...sliderSettings}>
            {gallery && gallery.map((image, index) => (                            
              <div 
                className="flex items-center justify-center bg-transparent px-2" 
                key={index}
              >
                <div className="relative w-full max-w-4xl mx-auto"> {/* Container with max width */}
                  <img 
                    className="mx-auto max-w-full max-h-[11vh] sm:max-h-[28vh] md:max-h-[38vh] object-contain"
                    style={{ 
                      imageRendering: 'auto',
                      width: 'auto',
                      height: 'auto'
                    }}
                    src={image} 
                    alt={`${role} image ${index + 1}`}
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </Slider>
        </div>
        <div className="w-full md:col-span-1 md:w-11/12 bg-blue-950/40 border border-blue-900/10 rounded-xl p-4 md:p-6 mb-4 md:mb-6">                      
          <div className="mb-4 md:mb-6">
            <h5 className="flex flex-row text-base md:text-lg font-semi text-white mb-3 md:mb-4">
              <Download size={18} className="mt-0.5 md:mt-1 mr-2 md:mr-3 text-blue-300" />
              Certificate Actions
            </h5>
            <div className="flex flex-col space-y-2 md:space-y-3 mb-2 md:mb-3">
              <button 
                onClick={handleDownload}
                disabled={!downloadUrl || downloadUrl === '#'}
                className="text-center space-x-2 bg-gradient-to-br hover:bg-gradient-to-tr from-blue-500 to-indigo-500 border border-blue-500/30 text-white px-3 py-2 md:px-4 md:py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
              >
                <Download size={16} className="inline-block -mt-1" />
                <span>Download as PDF</span>
              </button>
              <p className="text-white/60 text-xs md:text-sm">
                Official verification document
              </p>
              <button onClick={handleShareLinkedIn} className="text-center space-x-2 bg-white hover:bg-blue-500/30 border border-blue-500/30 text-blue-500 hover:text-white px-3 py-2 md:px-4 md:py-3 rounded-lg transition-colors text-sm md:text-base">
                <Share2 size={16} className="inline-block -mt-1" />
                <span>Share on LinkedIn</span>
              </button>
              <p className="text-white/60 text-xs md:text-sm">
                Add to your professional profile
              </p>
            </div>
          </div>

          {/* Verification Badge */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 md:p-4">
            <div className="flex items-center space-x-2 mb-2">
              <CircleCheckBig size={16} className="text-green-400" />
              <span className="text-green-400 font-normal text-sm md:text-base">Verified Passport</span>
            </div>
            <p className="text-white/80 text-xs md:text-sm">
              Officially verified by {verifiedBy}
            </p>
          </div>
        </div>
      </div>
      {/* Verification URL */}
      {/* <div className=" bg-blue-950/40 border border-blue-900/10 rounded-xl p-4">
        <p className="text-white/80 text-sm mb-2">
          <ExternalLink size={18} className="inline-block -mt-1 mr-1 text-blue-300" />
          Verification URL:
        </p>
        <a 
          href={verificationUrl} 
          className="text-blue-300 hover:text-blue-200 break-all text-sm block mb-2"
        >
          {verificationUrl}
        </a>
        <p className="text-white/60 text-xs">
          Anyone can verify this credential using the above URL.
        </p>
      </div> */}
      {/* Shareable Credential Link */}
      <div className="bg-blue-950/40 border border-blue-900/10 rounded-xl p-3 md:p-4">
        <p className="text-white/80 text-xs md:text-sm mb-2">
          <ExternalLink size={16} className="inline-block -mt-1 mr-1 text-blue-300" />
          Shareable Credential Link
        </p>
        <a 
          href={credentialLink} 
          className="text-blue-300 hover:text-blue-200 break-all text-xs md:text-sm block mb-2"
        >
          {credentialLink}
        </a>
        <p className="text-white/60 text-xs">
          Anyone can view this credential using the above URL.
        </p>
      </div>
    </div>
  );
};

export default PassportCard;